import React, { useRef } from 'react';
import QRCode from 'react-qr-code';
import { Button } from '../common/Button';
import { Printer } from 'lucide-react';

interface TicketReceiptProps {
  ticketNumber: string;
  qrPayload: string;
  raffleName: string;
  customerName: string;
  agentName?: string;
  price: string;
  currency: string;
  drawDate?: string;
  purchaseDate: string;
}

export const TicketReceipt: React.FC<TicketReceiptProps> = ({
  ticketNumber, qrPayload, raffleName, customerName, agentName, price, currency, drawDate, purchaseDate,
}) => {
  const receiptRef = useRef<HTMLDivElement>(null);

  const handlePrint = () => {
    const content = receiptRef.current;
    if (!content) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>Ticket #${ticketNumber}</title>
      <style>
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body { font-family: 'Courier New', monospace; width: 80mm; margin: 0 auto; }
        @media print { @page { size: 80mm auto; margin: 0; } body { width: 80mm; } }
      </style></head><body>${content.innerHTML}</body></html>`);
    win.document.close();
    win.onload = () => { win.print(); win.close(); };
  };

  return (
    <div>
      {/* Receipt — 80mm thermal receipt style */}
      <div ref={receiptRef} style={{ width: '302px', fontFamily: "'Courier New', monospace", backgroundColor: '#fff', color: '#000', padding: '16px', border: '1px dashed #ccc' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', borderBottom: '1px dashed #999', paddingBottom: '10px', marginBottom: '10px' }}>
          <div style={{ fontSize: '18px', fontWeight: 'bold', letterSpacing: '2px' }}>RAFFLEBOWL</div>
          <div style={{ fontSize: '10px', color: '#666', marginTop: '2px' }}>www.rafflebowl.com</div>
        </div>

        {/* Raffle name */}
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', textTransform: 'uppercase' }}>{raffleName}</div>
        </div>

        {/* Divider */}
        <div style={{ borderBottom: '1px dashed #999', marginBottom: '10px' }} />

        {/* Ticket number — large */}
        <div style={{ textAlign: 'center', marginBottom: '12px' }}>
          <div style={{ fontSize: '10px', color: '#666' }}>TICKET NUMBER</div>
          <div style={{ fontSize: '20px', fontWeight: 'bold', letterSpacing: '1px', marginTop: '2px' }}>{ticketNumber}</div>
        </div>

        {/* Details */}
        <div style={{ fontSize: '11px', marginBottom: '10px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Customer:</span><span style={{ fontWeight: 'bold' }}>{customerName}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Price:</span><span style={{ fontWeight: 'bold' }}>{currency} {price}</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span>Purchased:</span><span>{purchaseDate}</span>
          </div>
          {drawDate && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Draw Date:</span><span>{drawDate}</span>
            </div>
          )}
          {agentName && (
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
              <span>Sold by:</span><span>{agentName}</span>
            </div>
          )}
        </div>

        {/* Divider */}
        <div style={{ borderBottom: '1px dashed #999', marginBottom: '12px' }} />

        {/* QR Code */}
        <div style={{ textAlign: 'center', marginBottom: '10px' }}>
          <QRCode value={qrPayload} size={160} level="M" />
          <div style={{ fontSize: '9px', color: '#999', marginTop: '6px' }}>SCAN TO VERIFY</div>
        </div>

        {/* Footer */}
        <div style={{ borderTop: '1px dashed #999', paddingTop: '8px', textAlign: 'center' }}>
          <div style={{ fontSize: '9px', color: '#666' }}>Keep this ticket safe.</div>
          <div style={{ fontSize: '9px', color: '#666' }}>Present for prize collection.</div>
          <div style={{ fontSize: '9px', color: '#999', marginTop: '4px' }}>Thank you for playing!</div>
        </div>
      </div>

      {/* Actions — outside the receipt */}
      <div className="flex gap-2 mt-3">
        <Button size="sm" variant="outline" onClick={handlePrint} fullWidth>
          <Printer className="h-3.5 w-3.5 mr-1" />Print
        </Button>
      </div>
    </div>
  );
};
